import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTheme } from '@/core/theme';
import {
  applyStatusBar, hideSplash, watchKeyboard, watchResume, markNativeDocument, isNativeApp,
} from '@/core/native';
import { DrillHost } from '@/components/drill';
import { useAndroidBack } from './useAndroidBack';
import { useT } from '@/core/i18n';

/* =============================================================================
 * Native shell
 * -----------------------------------------------------------------------------
 * Sits inside the router so it can navigate, and above every page so the status
 * bar and the back button behave the same everywhere.
 * ========================================================================== */

export function NativeShell() {
  const t = useT();
  const { resolvedScheme } = useTheme();
  const [exitHint, setExitHint] = useState(false);

  // Back goes up one page; only a second press at the root closes the app.
  useAndroidBack(useCallback(() => {
    setExitHint(true);
    window.setTimeout(() => setExitHint(false), 2000);
  }, []));

  // The status bar follows light and dark, so its icons never vanish into it.
  // Android drops the styling when the app returns from the background, so it
  // is reapplied on resume rather than set once at launch.
  useEffect(() => {
    void applyStatusBar(resolvedScheme);
    return watchResume(() => void applyStatusBar(resolvedScheme));
  }, [resolvedScheme]);

  useEffect(() => {
    // CSS reserves status bar height only inside the app.
    markNativeDocument();
    void watchKeyboard();
    // Hidden after the first render rather than on a timer: earlier shows a
    // white flash, later makes a fast launch feel slow.
    void hideSplash();
  }, []);

  return (
    <>
      <Outlet />

      {/* The detail panels, drawn here and not beside their state.
          -------------------------------------------------------------------
          DrillProvider holds the stack above the router so a route change
          cannot empty it. But a panel links to the page it summarises, and a
          `<Link>` with no router above it throws rather than degrading — which
          took the whole application down every time a figure was clicked.

          This element is the router's root: above every page, never unmounted
          by navigation, and inside the router context the links need. */}
      <DrillHost />

      {exitHint && isNativeApp() && (
        <div
          role="status"
          className="fixed bottom-24 inset-x-0 z-50 flex justify-center pointer-events-none px-6"
        >
          <span className="pop-enter bg-surface border border-line shadow-pop rounded-pill px-4 py-2 text-[12.5px] font-medium">
            {t('ns.pressBackAgain')}
          </span>
        </div>
      )}
    </>
  );
}
